import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WmPickListResponse,
  type WmPutawayTask,
  type WmTransfer,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Input } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';
import { showApiError } from '@/utils/apiError';
import { Link } from 'react-router-dom';

import { notify } from '@/core/toast/notify';
import { useProductOptions } from '@/hooks/useProductOptions';
export function WarehouseOperationsPage() {
  const { hasAny } = usePermissions();
  const canReceive = hasAny('WAREHOUSE_RECEIVE', 'WAREHOUSE_ADMIN');
  const { getLabel: getProductLabel } = useProductOptions();
  const [putaway, setPutaway] = useState<WmPutawayTask[]>([]);
  const [picks, setPicks] = useState<WmPickListResponse[]>([]);
  const [transfers, setTransfers] = useState<WmTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [binId, setBinId] = useState('');
  const [scanValue, setScanValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tasks, openPicks, openTransfers] = await Promise.all([
        warehouseApi.listOpenPutaway(),
        warehouseApi.listOpenPicks(),
        warehouseApi.listOpenTransfers(),
      ]);
      setPutaway(tasks);
      setPicks(openPicks);
      setTransfers(openTransfers);
    } catch (err) {
      showApiError(err, 'Failed to load warehouse operations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmPutaway = async (taskId: number) => {
    try {
      await warehouseApi.confirmPutaway(taskId, binId ? Number(binId) : undefined);
      notify.success('Putaway confirmed');
      load();
    } catch (err) {
      showApiError(err, 'Putaway failed');
    }
  };

  const recordScan = async () => {
    if (!scanValue.trim()) return;
    try {
      await warehouseApi.scan({
        barcodeValue: scanValue.trim(),
        operation: 'PUTAWAY',
      });
      notify.success('Scan recorded');
      setScanValue('');
    } catch (err) {
      showApiError(err, 'Scan failed');
    }
  };

  return (
    <div className="pb-safe max-w-full overflow-x-hidden">
      <PageHeader
        title="Floor operations"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Operations' },
        ]}
        actions={
          <div className="flex gap-2">
            <ButtonLink to="/warehouse/mobile" variant="secondary">
              Mobile WMS
            </ButtonLink>
            <Button variant="secondary" onClick={load} loading={loading}>
              Refresh
            </Button>
          </div>
        }
      />

      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        Confirm putaway and log barcodes here. Inventory on-hand updates when movements complete via the
        ledger bridge.
      </p>

      <div className="card mb-6 p-4">
        <h2 className="mb-3 text-lg font-semibold">Scanner / barcode</h2>
        {canReceive ? (
          <div className="flex flex-wrap gap-2">
            <Input
              className="min-w-[200px] flex-1"
              placeholder="Scan barcode"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && recordScan()}
            />
            <Button onClick={recordScan}>Record scan</Button>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Receiving permission required to record scans.</p>
        )}
      </div>

      {(picks.length > 0 || transfers.length > 0) && (
        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          {picks.length > 0 && (
            <div className="card p-4">
              <h2 className="mb-2 text-lg font-semibold">Open pick lists</h2>
              <ul className="space-y-2 text-sm">
                {picks.map((p) => (
                  <li key={p.id}>
                    {p.pickCode} · {p.tasks.length} task(s) ·{' '}
                    <Link to="/warehouse/picks" className="text-primary-600">
                      Manage picks
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {transfers.length > 0 && (
            <div className="card p-4">
              <h2 className="mb-2 text-lg font-semibold">Open transfers</h2>
              <ul className="space-y-2 text-sm">
                {transfers.map((t) => (
                  <li key={t.id}>
                    {t.transferCode} · {t.statusCode} ·{' '}
                    <Link to="/warehouse/transfers" className="text-primary-600">
                      Manage transfers
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="card p-4">
        <h2 className="mb-3 text-lg font-semibold">Open putaway tasks</h2>
        <div className="mb-3">
          {canReceive && (
            <Input
              label="Target bin ID (optional)"
              value={binId}
              onChange={(e) => setBinId(e.target.value)}
            />
          )}
        </div>
        {putaway.length === 0 ? (
          <p className="text-sm text-slate-500">No open putaway tasks. Post a GRN to create receiving work.</p>
        ) : (
          <ul className="space-y-3">
            {putaway.map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700"
              >
                <div>
                  <p className="font-medium">{t.taskCode}</p>
                  <p className="text-xs text-slate-500">
                    Product {getProductLabel(t.productId, true)} · Qty {t.quantity}
                    {t.recommendedBinId ? ` · Bin ${t.recommendedBinId}` : ''}
                  </p>
                </div>
                {canReceive ? (
                  <Button className="min-h-[44px] min-w-[120px]" onClick={() => confirmPutaway(t.id)}>
                    Confirm putaway
                  </Button>
                ) : (
                  <span className="text-xs text-slate-500">Receive permission required</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
